// Pure, framework-free SSE frame parser + streaming POST helper.
// EventSource is NOT usable for the investigate stream because it cannot set an
// Authorization: Bearer header (REF-05 s.5), so the stream is consumed via
// fetch + response.body.getReader() + TextDecoder, framing on a blank line (\n\n).
import { API_CONFIG } from '../../config/api';

export type StreamEventType = 'created' | 'step' | 'report' | 'error' | 'done';

export interface RawStreamEvent {
  event: StreamEventType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}

/**
 * Parse a buffer of accumulated text into complete events; return [events, rest].
 * Frames are separated by a blank line (\n\n). Each frame has `event:` and `data:`
 * lines (REF-05 s.2). The trailing (possibly partial) frame is returned as `rest`
 * so the caller can keep buffering it across chunks.
 */
export function parseFrames(buffer: string): { events: RawStreamEvent[]; rest: string } {
  const events: RawStreamEvent[] = [];
  // Normalize CRLF → LF so framing is robust regardless of server line endings.
  const normalized = buffer.replace(/\r\n/g, '\n');
  const parts = normalized.split('\n\n');
  const rest = parts.pop() ?? ''; // last part may be an incomplete frame → keep buffering
  for (const part of parts) {
    if (!part.trim()) continue; // skip keep-alive blank frames
    let eventType: string | undefined;
    let dataLine = '';
    for (const line of part.split('\n')) {
      if (line.startsWith(':')) continue; // SSE comment / heartbeat
      if (line.startsWith('event:')) {
        eventType = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLine += line.slice('data:'.length).trim();
      }
    }
    if (!eventType) continue; // not a data event (e.g. bare comment block)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let data: any = null;
    try {
      data = dataLine ? JSON.parse(dataLine) : {};
    } catch {
      data = { _parseError: dataLine };
    }
    events.push({ event: eventType as StreamEventType, data });
  }
  return { events, rest };
}

export interface PostEventStreamOptions {
  token: string | null;
  signal: AbortSignal;
  onEvent: (e: RawStreamEvent) => void;
}

/**
 * Streaming POST helper. Opens the SSE stream, reads it frame by frame, and invokes
 * `onEvent` for each parsed event. Pre-stream validation errors (REF-05 s.1) arrive as
 * JSON, not a stream — those are synthesized into an `error` + `done` event pair so the
 * caller's reducer can treat every terminal condition uniformly.
 */
export async function postEventStream(
  path: string,
  body: unknown,
  opts: PostEventStreamOptions,
): Promise<void> {
  const res = await fetch(`${API_CONFIG.baseURL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(opts.token ? { Authorization: `Bearer ${opts.token}` } : {}),
    },
    body: JSON.stringify(body),
    signal: opts.signal,
    credentials: 'include',
  });

  // Pre-stream validation / auth / scope errors arrive as JSON, not a stream.
  if (!res.ok) {
    let code = 'AI_STREAM_HTTP_ERROR';
    let message = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      code = j.error_code || j.errorCode || code;
      message = j.error || j.message || message;
    } catch {
      /* keep defaults */
    }
    opts.onEvent({ event: 'error', data: { code, message } });
    opts.onEvent({ event: 'done', data: {} });
    return;
  }

  if (!res.body) {
    opts.onEvent({
      event: 'error',
      data: { code: 'AI_STREAM_UNSUPPORTED', message: 'No response body' },
    });
    opts.onEvent({ event: 'done', data: {} });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = parseFrames(buffer);
      buffer = rest;
      for (const ev of events) opts.onEvent(ev);
    }
    // Flush any trailing complete frame (append a frame separator so a buffered
    // final frame that lacked its terminating blank line still parses).
    buffer += decoder.decode();
    const { events } = parseFrames(buffer + '\n\n');
    for (const ev of events) opts.onEvent(ev);
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* already released */
    }
  }
}
